import { useEffect, useState, useCallback } from 'react'
import {
  Gamepad2, RefreshCw, Settings2, Search, Eye, EyeOff, ShieldOff, ShieldCheck,
  Download, Trash2, Save, X, AlertTriangle, CheckCircle2, ExternalLink, Server,
  Package, PackageOpen, Image as ImageIcon, Film,
} from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import PageHeader from '@/components/PageHeader'
import { gamesAPI } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import usePageTitle from '@/lib/usePageTitle'

/* Admin voor de games-library.
   Lijst-view met alle games (incl verborgen/missing), filter/search,
   edit-dialog per game, IGDB-refetch, scan-trigger, config-panel. */

const CATS = ['FPS/Action', 'RTS', 'Racing', 'Party', 'Sandbox', 'Andere']
const gb = (b) => b ? (Number(b) / 1e9).toFixed(1) : '—'

export default function GamesAdminPage() {
  usePageTitle('Games — beheer')
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'

  const [games, setGames] = useState([])
  const [config, setConfig] = useState({})
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [scanning, setScanning] = useState(false)
  const [edit, setEdit] = useState(null)      // game object of null
  const [showConfig, setShowConfig] = useState(false)
  const [extractJob, setExtractJob] = useState(null)  // { game, status, percent, bytes_done, bytes_total, tool, error }
  const [mediaPick, setMediaPick] = useState(null)    // game object of null (opent MediaPickerDialog)
  const [bgJobs, setBgJobs] = useState([])           // achtergrond-extract-jobs voor de badge

  const load = useCallback(async () => {
    try {
      const [g, c] = await Promise.all([gamesAPI.adminList(), gamesAPI.adminGetConfig()])
      setGames(g.data.games || [])
      setConfig(c.data.config || {})
      setErr(null)
    } catch (e) { setErr(e.response?.data?.error ?? e.message) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { if (isAdmin) load() }, [isAdmin, load])

  const scannen = async () => {
    setScanning(true)
    try { await gamesAPI.adminScan(); await new Promise(r => setTimeout(r, 1500)); await load() }
    catch (e) { alert('Scan mislukt: ' + (e.response?.data?.error || e.message)) }
    finally { setScanning(false) }
  }

  const refetchAll = async () => {
    if (!confirm(`Alle ${games.length} games opnieuw ophalen bij IGDB (met poster/trailer download)?\n\nDit duurt gemakkelijk 5-10 min op de achtergrond. Admin-overrides blijven behouden.`)) return
    try {
      await gamesAPI.adminRefetchAll()
      alert('Refetch gestart op de achtergrond. Check terug over enkele minuten voor de resultaten.')
    } catch (e) { alert(e.response?.data?.error || e.message) }
  }

  const uitpakken = async (g, { force = false } = {}) => {
    const bericht = force
      ? `"${g.name}" OPNIEUW uitpakken?\n\nDe huidige local/ folder wordt eerst gewist (bv na een gestopte/incomplete extract).\n\nExtract loopt op achtergrond met progress-bar.`
      : `.eti van "${g.name}" uitpakken naar local/?\n\nExtract loopt op achtergrond met progress-bar; venster sluiten laat extract doorlopen maar verliest de progress-view.`
    if (!confirm(bericht)) return
    try {
      const r = await gamesAPI.adminExtractEti(g.id, force)
      if (!r.data.started) {
        alert(r.data.reason || 'Kon niet starten'); return
      }
      setExtractJob({ game: g, status: 'running', percent: 0, bytes_done: 0, bytes_total: 0 })
    } catch (e) { alert('Extract fout: ' + (e.response?.data?.error || e.message)) }
  }

  // Achtergrond-extract-jobs pollen elke 5s zodat de admin ziet wat er nog
  // loopt na op "Achtergrond" te klikken. Ook nuttig na een frontend-refresh.
  useEffect(() => {
    if (!isAdmin) return
    let alive = true
    const laad = () => gamesAPI.adminExtractJobs()
      .then(r => { if (alive) setBgJobs(r.data.jobs || []) })
      .catch(() => {})
    laad()
    const iv = setInterval(laad, 5000)
    return () => { alive = false; clearInterval(iv) }
  }, [isAdmin])

  // Poll extract-status elke 2s zolang er een job in view is en die running is.
  useEffect(() => {
    if (!extractJob?.game || extractJob.status !== 'running') return
    const gameId = extractJob.game.id
    const iv = setInterval(async () => {
      try {
        const r = await gamesAPI.adminExtractStatus(gameId)
        if (!r.data.job) return
        setExtractJob(prev => prev ? { ...prev, ...r.data.job } : prev)
        if (r.data.job.status !== 'running') {
          clearInterval(iv)
          if (r.data.job.status === 'done') load()  // refresh badges (needs_extract off)
        }
      } catch {}
    }, 2000)
    return () => clearInterval(iv)
  }, [extractJob?.game?.id, extractJob?.status, load])

  const refetchIgdb = async (id) => {
    try {
      const r = await gamesAPI.adminFetchIgdb(id)
      alert(r.data.matched
        ? `IGDB match — ${r.data.updated || 0} velden bijgewerkt.`
        : 'Geen IGDB-match voor deze naam. Wijzig de naam en probeer opnieuw.')
      load()
    } catch (e) { alert('IGDB fout: ' + (e.response?.data?.error || e.message)) }
  }

  const toggleVisible = async (g) => {
    try { await gamesAPI.adminUpdate(g.id, { is_visible: !g.is_visible }); load() }
    catch (e) { alert(e.message) }
  }
  const toggleServerHidden = async (g) => {
    try { await gamesAPI.adminUpdate(g.id, { server_hidden: !g.server_hidden }); load() }
    catch (e) { alert(e.message) }
  }

  const verwijder = async (g) => {
    if (!confirm(`"${g.name}" uit de library verwijderen?\n(De game-folder op TrueNAS blijft staan — bij volgende scan komt hij terug tenzij je 'm verborgen zet.)`)) return
    try { await gamesAPI.adminDelete(g.id); load() }
    catch (e) { alert(e.message) }
  }

  if (!isAdmin) return <div className="p-6 text-muted-foreground">Enkel voor admins.</div>
  if (loading) return <div className="p-6 text-muted-foreground">Laden…</div>

  const zichtbaar = games.filter(g => !q || g.name.toLowerCase().includes(q.toLowerCase()))
  const stats = {
    totaal: games.length,
    visible: games.filter(g => g.is_visible).length,
    missing: games.filter(g => g.is_missing).length,
    gefetcht: games.filter(g => g.igdb_fetched_at).length,
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <PageHeader
        icon={Gamepad2}
        eyebrow="Beheer"
        title="Games"
        subtitle="Library-beheer — scan, override, verbergen, IGDB-refetch"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowConfig(true)}>
              <Settings2 className="h-4 w-4 mr-1.5" /> Config
            </Button>
            <Button variant="outline" size="sm" onClick={refetchAll}>
              <Download className="h-4 w-4 mr-1.5" /> IGDB refetch alle
            </Button>
            <Button size="sm" onClick={scannen} disabled={scanning}>
              <RefreshCw className={`h-4 w-4 mr-1.5 ${scanning ? 'animate-spin' : ''}`} />
              {scanning ? 'Scannen…' : 'Scan nu'}
            </Button>
          </div>
        }
      />

      {err && <div className="text-signal text-sm">{err}</div>}

      {/* Achtergrond-extract-jobs banner — verschijnt zolang er lopende
          jobs zijn (ook na "Achtergrond" of pagina-refresh). Klik = opent
          de progress-dialog opnieuw voor die job. */}
      {bgJobs.filter(j => j.status === 'running' || j.status === 'error').length > 0 && (
        <div className="rounded border border-yellow-500/40 bg-yellow-500/5 p-3">
          <div className="flex items-center gap-2 mb-2">
            <PackageOpen className="h-4 w-4 text-yellow-400" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-yellow-400">
              Achtergrond-extracts ({bgJobs.filter(j => j.status === 'running').length} lopend
              {bgJobs.filter(j => j.status === 'error').length > 0 && `, ${bgJobs.filter(j => j.status === 'error').length} gefaald`})
            </span>
          </div>
          <div className="space-y-1.5">
            {bgJobs.filter(j => j.status !== 'done').map(j => (
              <button key={j.game_id} onClick={() =>
                setExtractJob({ game: { id: j.game_id, name: j.name }, ...j })
              } className="w-full text-left flex items-center gap-3 p-2 rounded hover:bg-yellow-500/10 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{j.name}</div>
                  <div className="text-[11px] font-mono text-muted-foreground tabular-nums">
                    {j.status === 'running'
                      ? `${j.percent || 0}% · ${((j.bytes_done||0)/1e9).toFixed(2)} / ${((j.bytes_total||0)/1e9).toFixed(2)} GB · ${j.tool || '…'}`
                      : j.status === 'error' ? `✗ ${j.error || 'fout'}` : j.status}
                  </div>
                </div>
                <div className="w-32 h-1.5 rounded-full bg-secondary overflow-hidden shrink-0">
                  <div className={`h-full transition-all ${j.status === 'error' ? 'bg-signal' : 'bg-yellow-400'}`}
                       style={{ width: `${j.status === 'error' ? 100 : (j.percent || 0)}%` }} />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <Stat label="Totaal"   value={stats.totaal} />
        <Stat label="Zichtbaar" value={stats.visible} kleur="#61E294" />
        <Stat label="Missing"  value={stats.missing} kleur={stats.missing ? '#FFB57A' : undefined} />
        <Stat label="IGDB-gefetcht" value={`${stats.gefetcht}/${stats.totaal}`} kleur="#B4FF39" />
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3">
        <div className="flex-1 max-w-md flex items-center gap-2 bg-card border border-border rounded px-3">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Zoek game…"
                 className="flex-1 bg-transparent border-none text-sm py-2 outline-none" />
        </div>
        <span className="ml-auto text-xs text-muted-foreground font-mono tabular-nums">
          {zichtbaar.length} van {games.length}
        </span>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-border">
                <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Naam</th>
                <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Categorie</th>
                <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Grootte</th>
                <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Status</th>
                <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Acties</th>
              </tr>
            </thead>
            <tbody>
              {zichtbaar.length === 0 && (
                <tr><td colSpan={5} className="p-6 text-center text-muted-foreground text-sm">Geen games.</td></tr>
              )}
              {zichtbaar.map(g => (
                <tr key={g.id} className="border-b border-border/40 hover:bg-accent/30">
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {g.poster_url
                        ? <img src={g.poster_url} className="w-8 h-8 rounded object-cover" alt="" />
                        : <div className="w-8 h-8 rounded bg-secondary" />}
                      <div>
                        <div className="font-semibold">{g.name}</div>
                        <div className="text-xs text-muted-foreground font-mono truncate max-w-[280px]" title={g.folder_path}>
                          {g.folder_path}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <Badge variant="outline">{g.category}</Badge>
                  </td>
                  <td className="p-3 font-mono text-xs tabular-nums text-muted-foreground">
                    {gb(g.size_bytes)} GB
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1 flex-wrap">
                      {!g.is_visible && <Badge className="bg-signal/20 text-signal border-signal/40">verborgen</Badge>}
                      {g.is_missing && <Badge className="bg-orange-500/20 text-orange-300 border-orange-500/40">missing</Badge>}
                      {g.needs_extract && <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/40">uitpakken nodig</Badge>}
                      {g.has_server && !g.server_hidden && <Badge className="bg-live/20 text-live border-live/40">server</Badge>}
                      {g.igdb_fetched_at && <Badge variant="outline" className="text-acid border-acid/40">IGDB</Badge>}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" title={g.is_visible ? 'Verbergen' : 'Tonen'} onClick={() => toggleVisible(g)}>
                        {g.is_visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4 text-signal" />}
                      </Button>
                      {g.has_server && (
                        <Button size="sm" variant="ghost" title={g.server_hidden ? 'Server-knop tonen' : 'Server-knop verbergen'} onClick={() => toggleServerHidden(g)}>
                          {g.server_hidden ? <ShieldOff className="h-4 w-4 text-muted-foreground" /> : <ShieldCheck className="h-4 w-4 text-live" />}
                        </Button>
                      )}
                      {g.needs_extract && (
                        <Button size="sm" variant="ghost" title="ETI .eti uitpakken naar local/" onClick={() => uitpakken(g)}>
                          <PackageOpen className="h-4 w-4 text-yellow-400" />
                        </Button>
                      )}
                      {g.has_eti && !g.needs_extract && (
                        <Button size="sm" variant="ghost" title="Opnieuw uitpakken (na incomplete extract)" onClick={() => uitpakken(g, { force: true })}>
                          <Package className="h-4 w-4 text-muted-foreground hover:text-yellow-400" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" title="IGDB refetch" onClick={() => refetchIgdb(g.id)}>
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" title="Poster / trailer kiezen" onClick={() => setMediaPick(g)}>
                        <ImageIcon className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" title="Bewerken" onClick={() => setEdit(g)}>
                        <Settings2 className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" title="Verwijderen" onClick={() => verwijder(g)}>
                        <Trash2 className="h-4 w-4 text-signal" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {edit && <EditDialog game={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load() }} />}
      {showConfig && <ConfigDialog config={config} onClose={() => setShowConfig(false)} onSaved={() => { setShowConfig(false); load() }} />}
      {extractJob && <ExtractProgressDialog job={extractJob} onClose={() => setExtractJob(null)} />}
      {mediaPick && (
        <MediaPickerDialog
          game={mediaPick}
          onClose={() => setMediaPick(null)}
          onSaved={() => { setMediaPick(null); load() }}
        />
      )}
    </div>
  )
}

/* ── Media picker (poster + trailer) ─────────────────────────────
   Twee-secties-modal: alle IGDB cover/artwork/screenshot als
   thumbnails links om als poster te kiezen, alle IGDB videos rechts
   (of eronder) om als trailer te kiezen. Bij "Toepassen" downloadt de
   server beide naar /app/uploads/game_previews/<slug>/ en zet ze als
   admin-override in de DB.

   Vereist dat de game al een igdb_id heeft — anders opent eerst
   IgdbSearchDialog zodat admin de juiste record kan aankoppelen. */
function MediaPickerDialog({ game, onClose, onSaved }) {
  const [igdbId, setIgdbId]     = useState(game.igdb_id || null)
  const [media, setMedia]       = useState(null)   // { name, posters[], videos[] }
  const [loading, setLoading]   = useState(false)
  const [err, setErr]           = useState(null)
  const [pickedPoster, setPickedPoster] = useState(null)  // url
  const [pickedVideo,  setPickedVideo]  = useState(null)  // url
  const [saving, setSaving]     = useState(false)
  const [showSearch, setShowSearch] = useState(!game.igdb_id)

  useEffect(() => {
    if (!igdbId) return
    setLoading(true); setErr(null)
    gamesAPI.adminIgdbMedia(igdbId)
      .then(r => setMedia(r.data.media))
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false))
  }, [igdbId])

  const koppelIgdb = (result) => {
    setShowSearch(false)
    setIgdbId(result.id)
    setPickedPoster(null); setPickedVideo(null)
  }

  const toepassen = async () => {
    if (!pickedPoster && !pickedVideo) { alert('Kies eerst een poster of trailer.'); return }
    setSaving(true)
    try {
      const payload = {}
      if (pickedPoster) payload.poster_url = pickedPoster
      if (pickedVideo)  payload.video_url  = pickedVideo
      const r = await gamesAPI.adminApplyMedia(game.id, payload)
      alert(`Toegepast: ${(r.data.applied || []).join(', ') || 'niets'}`)
      onSaved()
    } catch (e) {
      alert('Toepassen faalde: ' + (e.response?.data?.error || e.message))
    } finally { setSaving(false) }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <DialogTitle>Poster & trailer kiezen · {game.name}</DialogTitle>
              {media && (
                <div className="text-xs text-muted-foreground font-mono mt-1">
                  IGDB: {media.name} (id {igdbId})
                </div>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowSearch(true)}>
              <Search className="h-3.5 w-3.5 mr-1.5" /> Andere IGDB-game
            </Button>
          </div>
        </DialogHeader>

        {!igdbId && !showSearch && (
          <div className="text-sm text-muted-foreground py-6 text-center">
            Deze game heeft nog geen IGDB-koppeling.
            <div className="mt-2">
              <Button size="sm" onClick={() => setShowSearch(true)}>
                <Search className="h-4 w-4 mr-1.5" /> Zoek IGDB-record
              </Button>
            </div>
          </div>
        )}

        {loading && <div className="text-muted-foreground text-sm py-6">IGDB-media ophalen…</div>}
        {err && <div className="text-signal text-sm">{err}</div>}

        {media && !loading && (
          <div className="flex-1 overflow-y-auto space-y-6">
            {/* Poster-sectie */}
            <section>
              <div className="flex items-center gap-2 mb-2">
                <ImageIcon className="h-4 w-4 text-acid" />
                <h3 className="font-semibold text-sm">Poster</h3>
                <span className="text-xs text-muted-foreground font-mono">
                  ({media.posters.length} kandidaten — covers/artworks/screenshots)
                </span>
              </div>
              {media.posters.length === 0
                ? <div className="text-xs text-muted-foreground">Geen posters beschikbaar.</div>
                : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                    {media.posters.map(p => {
                      const selected = pickedPoster === p.url
                      return (
                        <button key={p.id} onClick={() => setPickedPoster(p.url)}
                          className={`relative rounded overflow-hidden border-2 transition-colors ${
                            selected ? 'border-acid ring-2 ring-acid/50' : 'border-border hover:border-acid/60'
                          }`}
                          title={`${p.kind} — ${p.id}`}>
                          <img src={p.thumb} className="w-full h-32 object-cover" alt={p.kind}
                               loading="lazy" onError={(e) => { e.currentTarget.style.opacity = '0.3' }} />
                          <span className="absolute top-1 left-1 text-[9px] font-mono uppercase tracking-widest bg-black/60 text-white px-1.5 py-0.5 rounded">
                            {p.kind}
                          </span>
                          {selected && (
                            <span className="absolute bottom-1 right-1 bg-acid text-black rounded-full p-0.5">
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                )}
            </section>

            {/* Video-sectie */}
            <section>
              <div className="flex items-center gap-2 mb-2">
                <Film className="h-4 w-4 text-acid" />
                <h3 className="font-semibold text-sm">Trailer</h3>
                <span className="text-xs text-muted-foreground font-mono">
                  ({media.videos.length} kandidaten — YouTube)
                </span>
              </div>
              {media.videos.length === 0
                ? <div className="text-xs text-muted-foreground">Geen videos beschikbaar op IGDB.</div>
                : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {media.videos.map(v => {
                      const selected = pickedVideo === v.url
                      return (
                        <button key={v.id} onClick={() => setPickedVideo(v.url)}
                          className={`relative rounded overflow-hidden border-2 text-left transition-colors ${
                            selected ? 'border-acid ring-2 ring-acid/50' : 'border-border hover:border-acid/60'
                          }`}>
                          <div className="relative">
                            <img src={v.thumb} className="w-full aspect-video object-cover" alt={v.name}
                                 loading="lazy" onError={(e) => { e.currentTarget.style.opacity = '0.3' }} />
                            <div className="absolute inset-0 grid place-items-center pointer-events-none">
                              <div className="bg-black/60 rounded-full p-2">
                                <Film className="h-4 w-4 text-white" />
                              </div>
                            </div>
                            {selected && (
                              <span className="absolute bottom-1 right-1 bg-acid text-black rounded-full p-0.5">
                                <CheckCircle2 className="h-3.5 w-3.5" />
                              </span>
                            )}
                          </div>
                          <div className="p-2">
                            <div className="text-xs font-semibold line-clamp-1">{v.name}</div>
                            <a href={`https://www.youtube.com/watch?v=${v.id}`} target="_blank" rel="noreferrer"
                               onClick={e => e.stopPropagation()}
                               className="text-[10px] text-muted-foreground font-mono hover:text-acid inline-flex items-center gap-1 mt-0.5">
                              op YouTube <ExternalLink className="h-2.5 w-2.5" />
                            </a>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
            </section>

            <div className="text-xs text-muted-foreground">
              Bij "Toepassen" wordt de gekozen poster + trailer gedownload naar lokaal
              (blijft werken zonder internet) en gemarkeerd als admin-override —
              een latere IGDB-refetch overschrijft dit niet meer.
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={toepassen} disabled={saving || (!pickedPoster && !pickedVideo)}>
            <Save className="h-4 w-4 mr-1.5" />
            {saving ? 'Downloaden…' : 'Toepassen'}
          </Button>
        </DialogFooter>
      </DialogContent>
      {showSearch && (
        <IgdbSearchDialog
          initialQuery={game.name}
          onClose={() => setShowSearch(false)}
          onPick={koppelIgdb}
        />
      )}
    </Dialog>
  )
}

/* ── Extract progress modal ─────────────────────────────────────── */
function ExtractProgressDialog({ job, onClose }) {
  const { game, status, percent = 0, bytes_done = 0, bytes_total = 0, files = 0, tool, error } = job
  const gb = (b) => (Number(b || 0) / 1e9).toFixed(2)
  const running = status === 'running'
  const done = status === 'done'
  const failed = status === 'error'

  return (
    <Dialog open onOpenChange={(o) => !o && !running && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {running && '.eti uitpakken…'}
            {done && 'Uitgepakt ✓'}
            {failed && 'Extract gefaald'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="text-sm font-semibold">{game.name}</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
              {tool ? `via ${tool}` : 'starten…'}
            </div>
          </div>

          {/* Progress bar */}
          <div>
            <div className="flex justify-between text-xs font-mono text-muted-foreground mb-1 tabular-nums">
              <span>{gb(bytes_done)} / {bytes_total ? `${gb(bytes_total)} GB` : '?'}</span>
              <span>{percent}%</span>
            </div>
            <div className="h-2 bg-secondary rounded overflow-hidden">
              <div className={`h-full transition-all ${failed ? 'bg-signal' : done ? 'bg-live' : 'bg-acid'}`}
                   style={{ width: `${done ? 100 : percent}%` }} />
            </div>
          </div>

          {done && (
            <div className="text-sm text-live flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              {files} bestanden uitgepakt · game speelbaar
            </div>
          )}
          {failed && (
            <div className="text-sm text-signal flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{error || 'Onbekende fout'}</span>
            </div>
          )}
          {running && (
            <div className="text-xs text-muted-foreground">
              Extract loopt op de achtergrond op de server. Je mag dit venster sluiten —
              extract stopt niet, maar je verliest de progress-view. Grote games (5+ GB)
              duren ~10-15 min afhankelijk van disk-snelheid.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={running}>
            {running ? 'Bezig…' : 'Sluiten'}
          </Button>
          {running && (
            <Button variant="ghost" onClick={onClose} className="text-muted-foreground">
              Achtergrond
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Stat({ label, value, kleur = '#B4FF39' }) {
  return (
    <div className="bg-card border border-border rounded-[6px] p-4" style={{ borderLeft: `3px solid ${kleur}` }}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-[Rajdhani] font-bold text-3xl tabular-nums mt-1" style={{ color: kleur }}>
        {value}
      </div>
    </div>
  )
}

/* ── IGDB search modal ─────────────────────────────────────────── */
function IgdbSearchDialog({ initialQuery, onClose, onPick }) {
  const [q, setQ] = useState(initialQuery || '')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)

  const zoek = async (term) => {
    if (!term?.trim()) return
    setLoading(true); setErr(null)
    try {
      const r = await gamesAPI.adminIgdbSearch(term)
      setResults(r.data.results || [])
    } catch (e) { setErr(e.response?.data?.error || e.message) }
    finally { setLoading(false) }
  }

  useEffect(() => { if (initialQuery) zoek(initialQuery) }, [initialQuery])

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader><DialogTitle>Zoek de juiste game op IGDB</DialogTitle></DialogHeader>

        <form onSubmit={e => { e.preventDefault(); zoek(q) }} className="flex gap-2">
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Naam van de game…" autoFocus />
          <Button type="submit" disabled={loading}>{loading ? 'Zoeken…' : 'Zoek'}</Button>
        </form>

        {err && <div className="text-signal text-sm mt-2">{err}</div>}

        <div className="flex-1 overflow-y-auto mt-3 space-y-2">
          {!loading && results.length === 0 && q && (
            <div className="text-muted-foreground text-sm py-8 text-center">Geen resultaten voor "{q}"</div>
          )}
          {results.map(r => (
            <button key={r.id} onClick={() => onPick(r)}
              className="w-full flex items-start gap-3 p-2 bg-card border border-border rounded hover:border-acid text-left transition-colors">
              {r.cover
                ? <img src={r.cover} className="w-14 h-20 object-cover rounded shrink-0" alt="" />
                : <div className="w-14 h-20 bg-secondary rounded shrink-0 grid place-items-center text-muted-foreground text-xs">geen cover</div>}
              <div className="flex-1 min-w-0">
                <div className="font-semibold">{r.name} {r.year && <span className="text-muted-foreground font-normal text-xs">({r.year})</span>}</div>
                {r.platforms && <div className="text-xs text-muted-foreground font-mono">{r.platforms}</div>}
                {r.summary && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.summary}</div>}
              </div>
            </button>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/* ── Edit dialog ────────────────────────────────────────────────── */
function EditDialog({ game, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: game.name || '',
    description: game.description || '',
    install_help: game.install_help || '',
    category: game.category || 'Andere',
    genre: game.genre || '',
    release_year: game.release_year || '',
    publisher: game.publisher || '',
    min_players: game.min_players || 1,
    max_players: game.max_players || 1,
    launch_cmd: game.launch_cmd || '',
    server_cmd: game.server_cmd || '',
    poster_url: game.poster_url || '',
    video_url: game.video_url || '',
    is_visible: game.is_visible,
    server_hidden: game.server_hidden,
    is_free: game.is_free,
  })
  const [saving, setSaving] = useState(false)
  const [showSearch, setShowSearch] = useState(false)

  const chooseIgdb = async (r) => {
    setShowSearch(false)
    try {
      const res = await gamesAPI.adminFetchIgdb(game.id, r.id)
      if (res.data.matched) {
        alert(`IGDB "${r.name}" toegepast — ${res.data.updated || 0} velden bijgewerkt.`)
        onSaved()
      } else {
        alert('Fetch faalde')
      }
    } catch (e) { alert(e.response?.data?.error || e.message) }
  }

  const bewaar = async () => {
    setSaving(true)
    try {
      const patch = { ...form }
      patch.release_year = patch.release_year ? Number(patch.release_year) : null
      patch.min_players = Number(patch.min_players) || 1
      patch.max_players = Number(patch.max_players) || 1
      await gamesAPI.adminUpdate(game.id, patch)
      onSaved()
    } catch (e) { alert(e.response?.data?.error || e.message) }
    finally { setSaving(false) }
  }

  const bind = (k) => ({ value: form[k], onChange: (e) => setForm(f => ({ ...f, [k]: e.target.value })) })

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <DialogTitle>Bewerken · {game.name}</DialogTitle>
            <Button variant="outline" size="sm" onClick={() => setShowSearch(true)}>
              <Search className="h-3.5 w-3.5 mr-1.5" /> Zoek IGDB
            </Button>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Naam</Label>
            <Input {...bind('name')} />
          </div>
          <div className="col-span-2">
            <Label>Beschrijving</Label>
            <textarea {...bind('description')} rows={4}
              className="w-full bg-card border border-border rounded px-3 py-2 text-sm outline-none focus:border-acid" />
          </div>
          <div className="col-span-2">
            <Label>Hoe opstarten <span className="text-muted-foreground text-xs">(NL, ondersteunt regelbreaks + `code`)</span></Label>
            <textarea {...bind('install_help')} rows={4}
              placeholder="Standaard: dubbelklik game_start.cmd, Multiplayer → Local Area Network, join op host-IP"
              className="w-full bg-card border border-border rounded px-3 py-2 text-sm font-mono outline-none focus:border-acid" />
          </div>

          <div>
            <Label>Categorie</Label>
            <select {...bind('category')} className="w-full bg-card border border-border rounded px-3 py-2 text-sm">
              {CATS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <Label>Genre <span className="text-muted-foreground text-xs">(vrije tekst)</span></Label>
            <Input {...bind('genre')} />
          </div>
          <div>
            <Label>Release-jaar</Label>
            <Input type="number" {...bind('release_year')} />
          </div>
          <div>
            <Label>Publisher</Label>
            <Input {...bind('publisher')} />
          </div>
          <div>
            <Label>Min. spelers</Label>
            <Input type="number" min={1} {...bind('min_players')} />
          </div>
          <div>
            <Label>Max. spelers</Label>
            <Input type="number" min={1} {...bind('max_players')} />
          </div>

          <div>
            <Label>Launch-command</Label>
            <Input {...bind('launch_cmd')} placeholder="game_start.cmd" />
          </div>
          <div>
            <Label>Server-command <span className="text-muted-foreground text-xs">(leeg = geen server)</span></Label>
            <Input {...bind('server_cmd')} placeholder="server_start.cmd" />
          </div>

          <div className="col-span-2">
            <Label>Poster URL</Label>
            <Input {...bind('poster_url')} placeholder="https://.../cover.jpg" />
            {form.poster_url && <img src={form.poster_url} className="mt-2 max-h-32 rounded border border-border" alt="" />}
          </div>
          <div className="col-span-2">
            <Label>Video URL <span className="text-muted-foreground text-xs">(YouTube embed of MP4)</span></Label>
            <Input {...bind('video_url')} placeholder="https://www.youtube.com/embed/xxxx" />
          </div>

          <div className="col-span-2 flex gap-6 pt-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_visible}
                     onChange={e => setForm(f => ({ ...f, is_visible: e.target.checked }))} />
              Zichtbaar in library
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.server_hidden}
                     onChange={e => setForm(f => ({ ...f, server_hidden: e.target.checked }))} />
              Server-knop verbergen
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_free}
                     onChange={e => setForm(f => ({ ...f, is_free: e.target.checked }))} />
              Legaal-free
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={bewaar} disabled={saving}>
            <Save className="h-4 w-4 mr-1.5" /> {saving ? 'Bezig…' : 'Bewaren'}
          </Button>
        </DialogFooter>
      </DialogContent>
      {showSearch && <IgdbSearchDialog initialQuery={form.name} onClose={() => setShowSearch(false)} onPick={chooseIgdb} />}
    </Dialog>
  )
}

/* ── Config dialog ──────────────────────────────────────────────── */
function ConfigDialog({ config, onClose, onSaved }) {
  const [form, setForm] = useState({
    scan_interval_minutes: config.scan_interval_minutes || '60',
    scan_root_path:        config.scan_root_path || '/mnt/nas/game_files',
    igdb_auto_fetch:       config.igdb_auto_fetch || 'true',
    default_install_help:  config.default_install_help || '',
  })
  const [saving, setSaving] = useState(false)

  const bewaar = async () => {
    setSaving(true)
    try { await gamesAPI.adminSetConfig(form); onSaved() }
    catch (e) { alert(e.response?.data?.error || e.message) }
    finally { setSaving(false) }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Games — configuratie</DialogTitle></DialogHeader>

        <div className="space-y-3">
          <div>
            <Label>Scan-interval (minuten)</Label>
            <Input type="number" min={1}
              value={form.scan_interval_minutes}
              onChange={e => setForm(f => ({ ...f, scan_interval_minutes: e.target.value }))} />
            <p className="text-xs text-muted-foreground mt-1">Hoe vaak de scan-service game_files/ opnieuw bekijkt.</p>
          </div>

          <div>
            <Label>Scan-pad</Label>
            <Input value={form.scan_root_path}
                   onChange={e => setForm(f => ({ ...f, scan_root_path: e.target.value }))} />
            <p className="text-xs text-muted-foreground mt-1">Pad binnen de backend-container. Default: /mnt/nas/game_files.</p>
          </div>

          <div>
            <Label>IGDB auto-fetch</Label>
            <select value={form.igdb_auto_fetch}
                    onChange={e => setForm(f => ({ ...f, igdb_auto_fetch: e.target.value }))}
                    className="w-full bg-card border border-border rounded px-3 py-2 text-sm">
              <option value="true">Aan — nieuwe games automatisch IGDB-metadata</option>
              <option value="false">Uit — enkel manueel refetchen</option>
            </select>
          </div>

          <div>
            <Label>Default "Hoe opstarten"</Label>
            <textarea rows={5} value={form.default_install_help}
              onChange={e => setForm(f => ({ ...f, default_install_help: e.target.value }))}
              className="w-full bg-card border border-border rounded px-3 py-2 text-sm font-mono outline-none focus:border-acid" />
            <p className="text-xs text-muted-foreground mt-1">Wordt getoond als een game geen eigen install-help heeft.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={bewaar} disabled={saving}>
            <Save className="h-4 w-4 mr-1.5" /> {saving ? 'Bezig…' : 'Bewaren'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
